import { markSupportShownOnce, routeSupportCoreEvent } from 'Design-system/app/static/scripts/support-widget';
import {
  SupportChannelSelectedProperties,
  SupportEventProperties,
  SupportSurface,
  SupportWidgetDismissedProperties
} from 'Douleutaras/app/lib/posthog/posthog.types';
import { User } from 'Douleutaras/types';

import { FloatingSupportState, ResolvedSupportSurface } from './surfaces';

export interface SupportCaptures {
  captureSupportWidgetShown(properties: SupportEventProperties): void;
  captureSupportWidgetClicked(properties: SupportEventProperties): void;
  captureSupportChannelSelected(properties: SupportChannelSelectedProperties): void;
  captureSupportWidgetDismissed(properties: SupportWidgetDismissedProperties): void;
}

type SupportUserProperties = Pick<SupportEventProperties, 'is_existing_user' | 'user_type' | 'customer_id'>;

/** Who is looking — shared by every support surface in this app. */
export const supportUserProperties = (user?: User): SupportUserProperties => ({
  is_existing_user: Boolean(user?.id),
  // Same vocabulary as the Django adapter: the raw session value, or `anonymous`.
  user_type: user?.user_type || 'anonymous',
  ...(user?.id ? { customer_id: `${user.id}` } : {})
});

/** The property bag every event on a floating surface carries. */
export const buildSupportEventProperties = (
  { policy, jobId }: ResolvedSupportSurface,
  state: FloatingSupportState,
  user?: User
): SupportEventProperties => ({
  surface: policy.surface,
  trigger: policy.trigger,
  support_available: state === 'available',
  ...supportUserProperties(user),
  ...(jobId ? { job_id: `${jobId}` } : {})
});

/**
 * Map the design-system core's internal events onto the four PostHog events (#5619).
 * The mapping is Design-system's `routeSupportCoreEvent`, shared with the Django widget;
 * `getProperties` is read per event so the bag tracks the current job and user.
 *
 * `markShown` keeps Shown to once per surface per session. PostHog's own dedup is not
 * enough here: the bag carries `job_id`, which differs between job pages.
 */
export const createSupportTracker = (
  getProperties: () => SupportEventProperties,
  captures: SupportCaptures,
  markShown: (surface: SupportSurface) => boolean = markSupportShownOnce
) => (event: string, payload?: object): void => {
  const properties = getProperties();

  routeSupportCoreEvent(event, payload, {
    shown: () => {
      if (markShown(properties.surface)) {
        captures.captureSupportWidgetShown(properties);
      }
    },
    clicked: () => captures.captureSupportWidgetClicked(properties),
    channelSelected: channel => captures.captureSupportChannelSelected({ ...properties, channel }),
    dismissed: (method, secondsOpen) =>
      captures.captureSupportWidgetDismissed({ ...properties, dismiss_method: method, seconds_open: secondsOpen })
  });
};
